import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { submitPayment } from "@/lib/payment";
import { useAuth } from "@/context/AuthContext";
import { AuthDialog } from "@/components/AuthDialog";
import { toast } from "sonner";
import { Loader2, Lock, Play, Images, Flame, ShieldCheck, ChevronLeft, ChevronRight, Music, X } from "lucide-react";

const euros = (c) => `${((c || 0) / 100).toFixed((c % 100) ? 2 : 0)} €`;

const MUSIC_FILE = {
  default: "/diaporama-default.mp3",
  serein: "/diaporama-serein.mp3",
  lumiere: "/diaporama-lumiere.mp3",
  recueil: "/diaporama-recueil.mp3",
  postinity: "/diaporama-postinity.mp3",
  clair: "/diaporama-clair.mp3",
  espoir: "/diaporama-espoir.mp3",
  paix: "/diaporama-paix.mp3",
  celeste: "/diaporama-celeste.mp3",
  tendresse: "/diaporama-tendresse.mp3",
  horizon: "/diaporama-horizon.mp3",
  memoire: "/diaporama-memoire.mp3",
  aurore: "/diaporama-aurore.mp3",
  silence: "/diaporama-silence.mp3",
  eternite: "/diaporama-eternite.mp3",
};
const musicSrc = (choice, ownReady, ownUrl) => {
  if (choice === "own") return ownReady && ownUrl ? ownUrl : `${process.env.PUBLIC_URL}${MUSIC_FILE.default}`;
  return `${process.env.PUBLIC_URL}${MUSIC_FILE[choice] || MUSIC_FILE.default}`;
};

export default function CeremonyAccess() {
  const { livingId } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [slide, setSlide] = useState(0);
  const [showSlideshow, setShowSlideshow] = useState(false);
  const [musicUrl, setMusicUrl] = useState(null);
  const polling = useRef(false);

  const load = async () => {
    try {
      const r = await api.get(`/living-access/ceremony/${livingId}`);
      setInfo(r.data);
      if (r.data.unlocked && r.data.has_video) loadVideo();
      if (r.data.unlocked && r.data.music_ready && r.data.music_choice === "own") loadMusic();
    } catch (e) {
      setError(e.response?.status === 404 ? t("po.ca_err_notfound") : t("po.ac_err_load"));
    }
  };

  const loadMusic = async () => {
    try {
      const r = await api.get(`/living-access/ceremony/${livingId}/music`, { responseType: "blob" });
      setMusicUrl((p) => { if (p) URL.revokeObjectURL(p); return URL.createObjectURL(r.data); });
    } catch { /* ignore */ }
  };

  const loadVideo = async () => {
    try {
      const r = await api.get(`/living-access/ceremony/${livingId}/video`, { responseType: "blob" });
      setVideoUrl((p) => { if (p) URL.revokeObjectURL(p); return URL.createObjectURL(r.data); });
    } catch { /* ignore */ }
  };

  useEffect(() => { if (!loading) load(); /* eslint-disable-next-line */ }, [loading, user, livingId]);

  useEffect(() => {
    const n = info?.photos?.length || 0;
    if (!showSlideshow || n < 2) return;
    const id = setInterval(() => setSlide((s) => (s + 1) % n), 3500);
    return () => clearInterval(id);
  }, [showSlideshow, info]);

  useEffect(() => {
    const order = params.get("order");
    if (order && user && !polling.current) {
      polling.current = true;
      let n = 0;
      const cleanup = () => { const p = new URLSearchParams(params); p.delete("order"); p.delete("paid"); setParams(p, { replace: true }); polling.current = false; };
      const poll = async () => {
        n += 1;
        try {
          const r = await api.get(`/living-pay/status/${order}`);
          if (r.data.status === "paid") { toast.success(t("po.ca_unlocked")); await load(); cleanup(); return; }
        } catch { /* keep */ }
        if (n >= 15) { cleanup(); return; }
        setTimeout(poll, 2000);
      };
      poll();
    }
    if (params.get("canceled")) { toast.info(t("po.ac_pay_canceled")); const p = new URLSearchParams(params); p.delete("canceled"); setParams(p, { replace: true }); }
    // eslint-disable-next-line
  }, [params, user]);

  const unlock = async () => {
    if (!user) { setAuthOpen(true); return; }
    setBusy(true);
    try {
      const r = await api.post("/living-pay/checkout", {
        product_type: "ceremony", ref: livingId,
        origin_url: window.location.origin, return_path: window.location.pathname,
      });
      if (["free", "simulated", "already"].includes(r.data.mode)) { toast.success(t("po.ca_unlocked")); await load(); }
      else submitPayment(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || t("po.ac_pay_fail")); }
    finally { setBusy(false); }
  };

  const download = () => {
    const token = localStorage.getItem("bdc_token");
    fetch(`${api.defaults.baseURL}/living-access/ceremony/${livingId}/download`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((b) => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = "dossier-ceremonie.zip"; a.click(); URL.revokeObjectURL(u); })
      .catch(() => toast.error(t("po.ac_dl_fail")));
  };

  if (error) return <Shell><div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-6 text-center text-sm text-red-300" data-testid="ceremony-access-error">{error}</div></Shell>;
  if (!info) return <Shell><div className="flex justify-center py-16"><Loader2 className="animate-spin text-amber-300" /></div></Shell>;

  return (
    <Shell>
      <div data-testid="ceremony-access-page">
        <div className="mb-3 flex justify-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-500/10 px-5 py-2 text-lg font-semibold text-amber-200"><Flame size={20} /> {t("po.ca_badge")}</div>
        </div>
        <h1 className="text-center text-2xl font-semibold text-foreground">{t("po.ac_tribute_to", { name: info.owner_name })}</h1>
        {info.text && <p className="mt-2 whitespace-pre-wrap text-center text-sm text-muted-foreground">{info.text}</p>}

        {(info.songs?.length > 0) && (
          <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="ceremony-songs">
            <div className="mb-2 flex items-center justify-center gap-2 text-sm text-amber-200"><Music size={15} /> {t("po.ca_songs_title")}</div>
            <ul className="space-y-1 text-sm text-foreground/85">
              {info.songs.map((s, i) => <li key={i} className="flex items-center justify-center gap-2"><Music size={12} className="text-amber-300/70" /> {s}</li>)}
            </ul>
          </div>
        )}

        {info.unlocked ? (
          <div className="mt-6 space-y-5" data-testid="ceremony-unlocked">
            {info.citation && <p className="whitespace-pre-wrap rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm leading-relaxed text-foreground/90">{info.citation}</p>}
            {(info.photos?.length > 0) && (
              <div>
                <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-black">
                  <img src={info.photos[slide]} alt="" className="max-h-[420px] w-full object-contain" />
                  {info.photos.length > 1 && (<>
                    <button onClick={() => setSlide((s) => (s - 1 + info.photos.length) % info.photos.length)} className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-1.5 text-white"><ChevronLeft size={18} /></button>
                    <button onClick={() => setSlide((s) => (s + 1) % info.photos.length)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-1.5 text-white"><ChevronRight size={18} /></button>
                  </>)}
                </div>
                <div className="mt-2 flex justify-center">
                  <Button size="sm" variant="outline" className="rounded-full border-amber-400/40 text-amber-200" onClick={() => { setSlide(0); setShowSlideshow(true); }} data-testid="ceremony-view-slideshow-btn"><Play size={14} className="mr-1.5" /> {t("po.ca_view_slideshow")}</Button>
                </div>
              </div>
            )}
            {info.has_video && (videoUrl ? <video src={videoUrl} controls className="max-h-[420px] w-full rounded-2xl bg-black" /> : <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 size={14} className="animate-spin" /> {t("po.ac_loading_video")}</div>)}
            <div className="flex justify-center pt-2">
              <Button variant="outline" className="rounded-full border-white/15" onClick={() => navigate("/ma-bougie")} data-testid="ceremony-close-btn"><X size={15} className="mr-2" /> {t("po.ca_close")}</Button>
            </div>
          </div>
        ) : (
          <div className="mt-6 rounded-3xl border border-amber-400/20 bg-amber-500/[0.05] p-6 text-center" data-testid="ceremony-locked">
            <Lock className="mx-auto mb-3 text-amber-300" size={30} />
            <div className="mb-3 flex justify-center gap-4 text-sm text-muted-foreground">
              {info.photos_count > 0 && <span className="inline-flex items-center gap-1"><Images size={14} /> {info.photos_count} photo{info.photos_count > 1 ? "s" : ""}</span>}
              {info.has_video && <span className="inline-flex items-center gap-1"><Play size={14} /> Vidéo</span>}
            </div>
            {info.reason === "needs_owner_payment" ? (
              <p className="text-sm text-muted-foreground">Ce dossier n'est pas encore disponible.</p>
            ) : !user ? (
              <>
                <p className="text-sm text-muted-foreground">Créez votre compte gratuit pour accéder au dossier de cérémonie.</p>
                <Button className="mt-4 w-full rounded-full bg-amber-500 py-3 font-semibold text-black hover:bg-amber-400" onClick={() => setAuthOpen(true)} data-testid="ceremony-account-btn"><ShieldCheck size={16} className="mr-2" /> Créer mon compte / me connecter</Button>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">Accédez à toutes ces informations (photos, vidéo, message) pour {euros(info.price_cents)}.</p>
                <Button className="mt-4 w-full rounded-full bg-amber-500 py-3 font-semibold text-black hover:bg-amber-400" onClick={unlock} disabled={busy} data-testid="ceremony-unlock-btn">{busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Lock size={16} className="mr-2" />} Débloquer pour {euros(info.price_cents)}</Button>
              </>
            )}
          </div>
        )}
      </div>
      {showSlideshow && (info.photos?.length > 0) && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4" data-testid="ceremony-view-slideshow-modal" onClick={() => setShowSlideshow(false)}>
          {info.music_on !== false && (
            <audio src={musicSrc(info.music_choice, info.music_ready, musicUrl)} autoPlay onEnded={() => setShowSlideshow(false)} data-testid="ceremony-view-slideshow-audio" />
          )}
          <button className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20" onClick={() => setShowSlideshow(false)} data-testid="ceremony-view-slideshow-close"><X size={22} /></button>
          <img key={slide} src={info.photos[slide]} alt="" className="slide-fade max-h-[82vh] max-w-full rounded-xl object-contain" onClick={(e) => e.stopPropagation()} />
          {info.photos.length > 1 && (<>
            <button onClick={(e) => { e.stopPropagation(); setSlide((s) => (s - 1 + info.photos.length) % info.photos.length); }} className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white hover:bg-white/20"><ChevronLeft size={24} /></button>
            <button onClick={(e) => { e.stopPropagation(); setSlide((s) => (s + 1) % info.photos.length); }} className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white hover:bg-white/20"><ChevronRight size={24} /></button>
            <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 gap-1.5">{info.photos.map((_, i) => <span key={i} className={`h-2 w-2 rounded-full ${i === slide ? "bg-amber-300" : "bg-white/40"}`} />)}</div>
          </>)}
        </div>
      )}
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} initialMode="register" onSuccess={() => { setAuthOpen(false); load(); }} />
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="living-sky relative min-h-screen">
      <div className="living-sky-bg" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/living-sky.jpg)` }} aria-hidden="true" />
      <div className="sky-clouds" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/sky-clouds.png)` }} aria-hidden="true" />
      <div className="sky-clouds layer2" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/sky-clouds.png)` }} aria-hidden="true" />
      <Header />
      <div className="relative z-10 mx-auto max-w-lg px-5 py-10">
        <div className="rounded-3xl border border-white/25 bg-slate-900/55 p-5 shadow-xl backdrop-blur-xl">{children}</div>
      </div>
    </div>
  );
}
